import React, { useEffect, useState } from 'react'
import { useTypedSelector } from 'src/State/RootReducer'
import { makeStyles } from 'tss-react/mui'
import { t } from 'i18next'
import useModals from 'src/Hooks/useModals'
import { useBlocker } from 'react-router-dom'
import { Box, Hidden, IconButton, useMediaQuery, useTheme } from '@mui/material'
import { J1LeftarrowD } from '@e3dc-react/icons'
import { useDispatch } from 'react-redux'
import { appUpdate } from '@e3dc-react/shell/State/App/AppActions'

interface StyleProps {
  upLg: boolean
  downSm: boolean
}

interface SystemConfigurationProps {
  content: React.ReactNode
  bottomBar: React.ReactNode
  sidebarCard: (onClose: () => void) => React.ReactNode
  mobileSidebarCard: (onClose: () => void) => React.ReactNode
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  container: {
    position: 'relative',
    marginTop: theme.spacing(4),
    width: '100%',
  },
  systemContainer: {
    maxWidth: '100%',
    display: 'grid',
    paddingRight: 0,
    padding: '0 2px',
    gridTemplateColumns: 'minmax(0, 1fr) 270px',
    overflow: 'hidden',
    overflowX: 'hidden',
    [theme.breakpoints.only('xs')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  systemConfigurationCard: {
    display: props.downSm ? 'none' : 'inherit',
    position: 'fixed',
    minHeight: '100vh',
    maxHeight: '100vh',
    right: theme.spacing(2),
    top: theme.spacing(16),
    [theme.breakpoints.down('md')]: {
      top: theme.spacing(14),
    },
    minWidth: '260px',
    maxWidth: '260px',
  },
  bottomProgressBar: {
    position: 'fixed',
    width: 'auto',
    bottom: theme.spacing(2),
    left: theme.shape.header.left,
    right: props.upLg ? '282px' : theme.spacing(2),
    padding: '0 2px',
    zIndex: 1,
    transition: theme.transitions.create('left', {
      easing: theme.transitions.easing.easeInOut,
      duration: theme.transitions.duration.standard,
    }),
    [theme.breakpoints.down('md')]: {
      left: theme.spacing(2),
    },
    [theme.breakpoints.only('xs')]: {
      bottom: theme.spacing(10),
    },
  },
  bottomProgressBarWide: {
    left: theme.shape.header.leftCollapsed,
    [theme.breakpoints.down('md')]: {
      left: theme.spacing(1),
    },
  },
  expandIcon: {
    position: 'fixed',
    right: 0,
    top: theme.spacing(16),
    zIndex: 10,
    backgroundColor: theme.palette.primary.main,
    borderTopLeftRadius: '50%',
    borderBottomLeftRadius: '50%',
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    padding: 0,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
}))

/*
 * Layout for system configuration with content, sidebar card, and bottom progress bar
 */
const SystemConfiguration: React.FC<SystemConfigurationProps> = ({ content, bottomBar, sidebarCard, mobileSidebarCard }) => {
  const theme = useTheme()
  const collapsedSidebar = useTypedSelector(state => state.app.sidebarCollapsed)
  const { openShopLeavingConfirmation } = useModals()
  const dispatch = useDispatch()
  const [show, setShow] = useState<boolean>(false)
  const upLg = useMediaQuery(theme.breakpoints.up('lg'))
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const { classes, cx } = useStyles({ upLg, downSm })

  const handleOpenMobileSidebar = (): void => setShow(true)
  const handleCloseMobileSidebar = (): void => setShow(false)

  const blocker = useBlocker(({ nextLocation }) => {
    const navigationOutsideSystemConfigurator = nextLocation.search.includes('page') && nextLocation.pathname.includes('shop/system-configure')

    const navigationOutsideHomePage = nextLocation.search !== '?allow-redirect' && nextLocation.pathname !== 'shop/homepage'

    return navigationOutsideHomePage && !navigationOutsideSystemConfigurator
  })

  useEffect(() => {
    if (blocker.state === 'blocked') {
      dispatch(appUpdate({ moreActive: false }))
      openShopLeavingConfirmation({
        title: t('shop.leaving-configurator.title'),
        text: t('shop.leaving-configurator.exit-message'),
        confirmButtonText: t('shop.common.confirm'),
        cancelButtonText: t('shop.common.cancel'),
        onConfirm: async onClose => {
          onClose()
          blocker.proceed?.()
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocker])

  return (
    <Box className={classes.container}>
      <Hidden smUp>
        <IconButton className={classes.expandIcon} onClick={() => handleOpenMobileSidebar()}>
          <J1LeftarrowD sx={{ width: '36px', height: '36px' }} />
        </IconButton>
      </Hidden>

      <Box className={classes.systemContainer}>
        {content}

        <Box className={classes.systemConfigurationCard}>{sidebarCard(() => undefined)}</Box>
      </Box>

      {show && <Box sx={{ display: { xs: 'block', sm: 'none' } }}>{mobileSidebarCard(handleCloseMobileSidebar)}</Box>}

      <Box className={cx(classes.bottomProgressBar, { [classes.bottomProgressBarWide]: collapsedSidebar })}>{bottomBar}</Box>
    </Box>
  )
}

export default SystemConfiguration
